/**
 * useStripeRedirectHandler — Module 13 task 6 (KB C4 fix).
 *
 * Detects Stripe's redirect params (`payment_intent`, `redirect_status`,
 * `payment_intent_client_secret`) on mount and:
 *   - `succeeded`              → clears the PF revamp draft + invokes
 *                                 `onSuccess(paymentIntentId)` so the
 *                                 caller can navigate to a confirmation.
 *   - `processing` (SEPA)       → treated as success for UX; SEPA clears
 *                                 asynchronously on the BE.
 *   - anything else / failure   → calls `onError(status, reason)`.
 *
 * The actual final reconciliation lives in
 * `PaymentStatus.controller.tsx`, which is the canonical landing page
 * for Stripe return URLs across the app. This hook is purely a defensive
 * in-revamp cleanup in case the user lands back on the Summary URL with
 * Stripe params attached (e.g. browser back-nav from an aborted session).
 *
 * KB C4 was: the legacy Stripe flow never cleared the local PF draft on
 * successful placement, so the user could navigate back to /summary and
 * see a stale cart. This hook fixes that for the revamp tree.
 *
 * Path guard: only process Stripe params when the current pathname
 * is the Summary route. The hook is mounted at the PF root and would
 * otherwise fire on any PF route that happens to carry Stripe query params
 * (e.g. a direct deep link to /catalogue?payment_intent=...).
 *
 * `handledPaymentIntents` is module-level so it survives unmount /
 * remount of the Summary page (e.g. user navigates away and back). Without
 * this, the same payment_intent would be processed again on remount, causing
 * a spurious `resetPersistedState()` call that clears a freshly-started cart.
 */

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { Path } from 'constants/router/paths'

import { PF_SESSION_SEGMENT, PfStepPath } from './pfBasePath'
import { logger } from 'utils/logging'

import { usePfStore } from '../store'
import { PfRevampEventName, usePfRevampAnalytics } from '../analytics'

/**
 * Module-level set of payment_intent ids that have already been handled.
 * Survives unmount/remount of the component that calls this hook.
 */
const handledPaymentIntents = new Set<string>()

/** The Summary step under either mount point — the flow's own route, or the Propstack embed's — with or without a session segment. */
export const isSummaryPath = (pathname: string): boolean =>
  [Path.PURCHASE_FLOW_REVAMP, Path.EMBED_PURCHASE_FLOW].some((root) =>
    pathname === `${root}/${PfStepPath.SUMMARY}` ||
    new RegExp(`^${root}/${PF_SESSION_SEGMENT}/[^/]+/${PfStepPath.SUMMARY}$`).test(pathname))

export interface StripeRedirectParams {
  paymentIntent: string | null
  paymentIntentClientSecret: string | null
  redirectStatus: string | null
}

export interface UseStripeRedirectHandlerOptions {
  onSuccess?: (paymentIntent: string) => void
  onError?: (redirectStatus: string, paymentIntent: string | null) => void
}

const STRIPE_SUCCESS_STATUSES = new Set(['succeeded', 'processing'])

export const readStripeRedirectParams = (search: string): StripeRedirectParams => {
  const p = new URLSearchParams(search)
  return {
    paymentIntent: p.get('payment_intent'),
    paymentIntentClientSecret: p.get('payment_intent_client_secret'),
    redirectStatus: p.get('redirect_status'),
  }
}

export const useStripeRedirectHandler = (options: UseStripeRedirectHandlerOptions = {}): void => {
  const location = useLocation()
  const { onSuccess, onError } = options
  const trackAnalytics = usePfRevampAnalytics()

  useEffect(() => {
    // Only handle on a Summary route. Stripe redirects back to the return_url, which is
    // the Summary page; any other route receiving these params is unexpected and should not
    // trigger draft clearing. Both mount points count: an exact test against the main-app constant
    // left the EMBED's own summary unhandled, so a redirected Propstack order never settled.
    if (!isSummaryPath(location.pathname)) return

    const params = readStripeRedirectParams(location.search)
    if (!params.paymentIntent || !params.redirectStatus) return

    // Skip if this payment_intent was already processed (survives remount).
    if (handledPaymentIntents.has(params.paymentIntent)) return
    handledPaymentIntents.add(params.paymentIntent)

    if (STRIPE_SUCCESS_STATUSES.has(params.redirectStatus)) {
      logger.info('pf-revamp Stripe redirect: success — clearing draft', undefined, {
        context: { component: 'useStripeRedirectHandler', metadata: { paymentIntent: params.paymentIntent } },
      })

      // Clear the revamp draft so the user can't accidentally resume it.
      // Using `resetPersistedState` instead of a synthetic `hydrateFromDraft`
      // call: the synthetic DTO hardcoded `initiatedAs: 'CLIENT'`
      // which is wrong for admin-initiated orders, and calling `hydrateFromDraft`
      // without `setHydrating(true/false)` guards left `isHydrating` in an
      // inconsistent state.
      usePfStore.getState().resetPersistedState()

      trackAnalytics({
        type: PfRevampEventName.STRIPE_REDIRECT_SUCCEEDED,
        payload: { paymentIntent: params.paymentIntent },
      })

      onSuccess?.(params.paymentIntent)
      return
    }

    logger.warn('pf-revamp Stripe redirect: non-success status', undefined, {
      context: {
        component: 'useStripeRedirectHandler',
        metadata: { paymentIntent: params.paymentIntent, redirectStatus: params.redirectStatus },
      },
    })
    trackAnalytics({
      type: PfRevampEventName.STRIPE_REDIRECT_FAILED,
      payload: { paymentIntent: params.paymentIntent, status: params.redirectStatus },
    })
    onError?.(params.redirectStatus, params.paymentIntent)
  }, [location.pathname, location.search, onError, onSuccess, trackAnalytics])
}
